import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../config/config';
import { showErrorSnackbar } from './Snackbar';
import { getCookie } from './utils';

/**
 * CommHandler - Main Axios instance for API communication
 *
 * CSRF Protection:
 * This instance is configured to automatically handle CSRF tokens for security.
 * - withCredentials: true - Enables cookie transmission (required for CSRF token)
 * - xsrfCookieName: 'XSRF-TOKEN' - Cookie name where backend sets the CSRF token
 * - xsrfHeaderName: 'x-xsrf-token' - Header name where Axios sends the CSRF token
 *
 * Axios will automatically:
 * 1. Read the CSRF token from the XSRF-TOKEN cookie
 * 2. Add it as x-xsrf-token header on POST/PUT/DELETE/PATCH requests
 * 3. Send credentials (cookies) with every request
 *
 * See CSRF_IMPLEMENTATION.md for detailed documentation
 */

const defaultHeaders = {
  'Content-Type': 'application/json',
  'x-trace-id': uuidv4(),
};
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();
const financialYear = currentMonth >= 3 ? currentYear : currentYear - 1;
export const currentFinancialYear = `${financialYear}-${financialYear + 1}`;

/**
 * Factory function to create axios instances with common configuration
 */
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      ...defaultHeaders,
    },
    // Enable credentials to allow cookies (including CSRF token cookie)
    withCredentials: true,
  });

  // Configure automatic CSRF token handling
  instance.defaults.xsrfCookieName = 'XSRF-TOKEN'; // Cookie name set by backend
  instance.defaults.xsrfHeaderName = 'x-xsrf-token'; // Header name expected by backend

  const requestInterceptor = (request: InternalAxiosRequestConfig) => {
    request.headers.set('x-idToken', localStorage.getItem('token') || '');
    request.headers.set('x-user_id', localStorage.getItem('user_id') || '');
    request.headers.set('x-department', localStorage.getItem('department') || '');
    request.headers.set('user', localStorage.getItem('user') || '');

    // Always set CSRF header from cookie if present
    const xsrfToken = getCookie('XSRF-TOKEN');
    request.headers.set('x-xsrf-token', xsrfToken ?? '');

    // Explicitly ensure withCredentials is set for every request
    request.withCredentials = true;

    return request;
  };

  const responseSuccessInterceptor = (response: AxiosResponse) => {
    return Promise.resolve(response.data);
  };

  const responseErrorInterceptor = (err: AxiosError) => {
    if (err.response) {
      let errorMessage = 'Unknown Error';

      const response = err.response as { data?: { message?: string; status?: number; error?: string | [{ msg?: string }] }; statusText?: string };

      const isUserRoleApi = err.config?.url?.includes('/role/user-role');

      if (err.response?.status === 401) {
        window.location.href = '/';
        return Promise.reject(new Error('Unauthorized'));
      }

      // Handle different backend error response formats
      if (response.data?.error && typeof response.data.error === 'string') {
        // Handle case where error is a string (like foreign key constraint errors)
        const errorString = response.data.error;

        // Check for specific foreign key constraint error for form deletion
        if (errorString.includes('violates foreign key constraint "fk_form_id" on table "questioner_user"')) {
          errorMessage = 'User has already filled this form';
        } else {
          errorMessage = errorString;
        }
      } else if (response.data?.message) {
        // Handle case where message is provided
        errorMessage = response.data.message;
      } else if (response.data?.error && Array.isArray(response.data.error)) {
        // Handle case where error is an array (validation errors)
        errorMessage = response.data.error[0]?.msg || 'Validation error';
      } else {
        // Fallback to statusText
        errorMessage = err.response.statusText || 'Unknown Error';
      }

      const errorMessageCapitalized = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);

      if (!isUserRoleApi && !errorMessage.startsWith('The following titles already exist')) {
        showErrorSnackbar(errorMessageCapitalized);
      }

      return Promise.reject(new Error(errorMessageCapitalized));
    }
    const genericErrorMessage = 'Something went wrong';

    const isUserRoleApi = err.config?.url?.includes('/role/user-role');
    if (!isUserRoleApi) {
      showErrorSnackbar(genericErrorMessage);
    }

    return Promise.reject(new Error(genericErrorMessage));
  };

  instance.interceptors.request.use(requestInterceptor);
  instance.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);

  return instance;
};

const CommHandler: AxiosInstance = createAxiosInstance(config.apiBaseUrl);

// Create axios instance for RBAC API
export const RbacCommHandler: AxiosInstance = createAxiosInstance(config.rbacApiBaseUrl);

export const getcurrentFinancialYear = () => {
  try {
    const year = new Date().getFullYear();
    const month = new Date().getMonth();
    const finYear = month >= 3 ? year : year - 1;
    const finYearString = `${finYear}-${finYear + 1}`;
    return finYearString.toString();
  } catch (error) {
    console.error('Error calculating current financial year:', error);
    return 'Unknown';
  }
};

export default CommHandler;
